import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { protocolService } from '../services/protocolService'
import { formatDate } from '../utils/locale'
import { Modal } from '../components/Modal'
import { useAuth } from '../contexts/AuthContext'
import './ProtocolDetail.css'

export function ProtocolDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const [showEligibleModal, setShowEligibleModal] = useState(false)

  const { data: protocol, isLoading, error } = useQuery({
    queryKey: ['protocol', id],
    queryFn: () => protocolService.getById(id!),
    enabled: !!id,
  })

  const deleteMutation = useMutation({
    mutationFn: () => protocolService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocols'] })
      navigate('/protocols')
    },
  })

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this protocol? This action cannot be undone.')) {
      deleteMutation.mutate()
    }
  }

  if (isLoading) return <div className="protocol-detail-loading">Loading protocol...</div>
  if (error) return <div className="protocol-detail-error">Error loading protocol</div>
  if (!protocol) return <div className="protocol-detail-error">Protocol not found</div>

  return (
    <div className="protocol-detail">
      <div className="protocol-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/protocols')}>
            ← Back to Protocols
          </button>
          <div className="header-actions">
            {protocol.status === 'active' && hasPermission('protocols:read') && (
              <button
                className="btn-secondary"
                onClick={() => setShowEligibleModal(true)}
              >
                Find Eligible Patients
              </button>
            )}
            <button
              className="btn-secondary"
              onClick={() => navigate(`/protocols/${id}/edit`)}
            >
              Edit
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/protocols/${id}/compliance`)}
            >
              Compliance
            </button>
            <button className="btn-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
        <div className="header-title-section">
          <h1>{protocol.name}</h1>
          <div className="protocol-meta">
            <span className={`status-badge status-${protocol.status}`}>{protocol.status}</span>
            {protocol.category && (
              <span className="protocol-category">{protocol.category}</span>
            )}
            {protocol.version && (
              <span className="protocol-version">v{protocol.version}</span>
            )}
          </div>
        </div>
      </div>

      <div className="protocol-detail-content">
        {protocol.description && (
          <section className="protocol-section">
            <h2>Description</h2>
            <p>{protocol.description}</p>
          </section>
        )}

        <section className="protocol-section protocol-dates">
          <h2>Dates</h2>
          <div className="dates-grid">
            {protocol.effectiveDate && (
              <div>
                <span className="date-label">Effective:</span>{' '}
                {formatDate(protocol.effectiveDate)}
              </div>
            )}
            {protocol.expiryDate && (
              <div>
                <span className="date-label">Expiry:</span>{' '}
                {formatDate(protocol.expiryDate)}
              </div>
            )}
            <div>
              <span className="date-label">Created:</span>{' '}
              {formatDate(protocol.createdAt)}
            </div>
            <div>
              <span className="date-label">Updated:</span>{' '}
              {formatDate(protocol.updatedAt)}
            </div>
          </div>
        </section>

        {protocol.tags && protocol.tags.length > 0 && (
          <section className="protocol-section">
            <h2>Tags</h2>
            <div className="protocol-tags">
              {protocol.tags.map((tag) => (
                <span key={tag} className="tag-badge">
                  {tag}
                </span>
              ))}
            </div>
          </section>
        )}

        {protocol.rules && protocol.rules.length > 0 && (
          <section className="protocol-section">
            <h2>Rules</h2>
            <div className="rules-list">
              {protocol.rules
                .sort((a, b) => a.priority - b.priority)
                .map((rule) => (
                  <div key={rule.id} className="rule-card">
                    <h3>{rule.name}</h3>
                    {rule.description && <p>{rule.description}</p>}
                    {rule.actions && rule.actions.length > 0 && (
                      <ul className="actions-list">
                        {rule.actions.map((action) => (
                          <li key={action.title}>
                            {action.title}
                            {action.dueDateOffset != null && (
                              <span className="action-offset">
                                {' '}(due {action.dueDateOffset} days from enrollment)
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
            </div>
          </section>
        )}

        {(!protocol.rules || protocol.rules.length === 0) && (
          <section className="protocol-section protocol-empty-state">
            <p>No rules configured. Add rules to define protocol actions for enrolled patients.</p>
          </section>
        )}
      </div>

      <Modal
        isOpen={showEligibleModal}
        onClose={() => setShowEligibleModal(false)}
        title="Eligible Patients"
        size="large"
      >
        <EligiblePatientsModal
          protocolId={id!}
          protocolName={protocol.name}
          onClose={() => setShowEligibleModal(false)}
          onEnroll={() => queryClient.invalidateQueries({ queryKey: ['patient-protocols'] })}
        />
      </Modal>
    </div>
  )
}

function EligiblePatientsModal({
  protocolId,
  onClose,
}: {
  protocolId: string
  protocolName: string
  onClose: () => void
  onEnroll: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canWrite = useAuth().hasPermission('protocols:write')

  const { data: screenResult, isLoading, error } = useQuery({
    queryKey: ['protocol-eligible-patients', protocolId],
    queryFn: () => protocolService.screenEligiblePatients(protocolId),
    enabled: !!protocolId,
  })

  const enrollMutation = useMutation({
    mutationFn: (patientId: string) =>
      protocolService.enrollPatient(patientId, protocolId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocol-eligible-patients', protocolId] })
      queryClient.invalidateQueries({ queryKey: ['patient-protocols'] })
    },
  })

  if (isLoading) return <div className="eligible-modal-loading">Screening patients...</div>
  if (error) return <div className="eligible-modal-error">Failed to load eligible patients</div>

  const eligible = screenResult?.eligiblePatients ?? []
  const totalScreened = screenResult?.totalScreened ?? 0
  const alreadyEnrolled = screenResult?.alreadyEnrolled ?? 0

  return (
    <div className="eligible-patients-modal">
      <p className="eligible-modal-summary">
        Found {eligible.length} eligible patient{eligible.length !== 1 ? 's' : ''} (not already enrolled).
        Screened {totalScreened} patients. {alreadyEnrolled} already enrolled.
      </p>
      {eligible.length === 0 ? (
        <p className="eligible-modal-empty">
          No eligible patients found. Check protocol criteria or ensure patients have the required data.
        </p>
      ) : (
        <div className="eligible-patients-list">
          {eligible.map((p) => (
            <div key={p.patientId} className="eligible-patient-row">
              <button
                type="button"
                className="btn-patient-link"
                onClick={() => {
                  onClose()
                  navigate(`/patients/${p.patientId}`)
                }}
              >
                {p.firstName} {p.lastName}
              </button>
              <span className="eligible-patient-id">{p.patientId}</span>
              {canWrite && (
                <button
                  type="button"
                  className="btn-enroll-small"
                  onClick={() => enrollMutation.mutate(p.patientId)}
                  disabled={enrollMutation.isPending}
                >
                  {enrollMutation.isPending ? 'Enrolling...' : 'Enroll'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
